import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Content, EmptyState } from "@/components/PageChrome";
import { TablePagination } from "@/components/TablePagination";
import { Plus, Pencil, Trash2, X, FileText, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", email: "", phone: "", address: "",
  city: "", state: "", country: "India", postal_code: "", gstin: "", notes: "",
};

const PAGE_SIZE = 25;

/** Render any client field as text; never hand a raw object to React. */
function text(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "object") {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

export default function Clients() {
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // client obj or null
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // NOTE: this page deliberately does *not* read the active company.
  // Clients are shared by every company on the account, so switching company
  // must not change (or reload) this list.

  const load = useCallback(async (search, skip, size) => {
    setLoading(true);
    try {
      const params = { skip: skip || 0, limit: size || PAGE_SIZE };
      if (search) params.search = search;
      const [{ data }, count] = await Promise.all([
        api.get("/clients", { params }),
        api.get("/clients/count", { params: search ? { search } : {} }),
      ]);
      setList(Array.isArray(data) ? data : []);
      setTotal(count?.data?.count ?? (Array.isArray(data) ? data.length : 0));
    } catch (e) {
      toast.error(formatApiError(e));
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce so typing doesn't fire a request per keystroke. `debounced.text`
  // and `page` are the only inputs to the fetch effect, so each user action
  // produces exactly one request instead of one per effect.
  const [debounced, setDebounced] = useState({ text: "", size: PAGE_SIZE });

  useEffect(() => {
    const handle = setTimeout(() => {
      const needle = q.trim();
      setDebounced((d) => (d.text === needle ? d : { ...d, text: needle }));
      setPage(0);
    }, 250);
    return () => clearTimeout(handle);
  }, [q]);

  const changePageSize = (n) => {
    setDebounced((d) => ({ ...d, size: n }));
    setPage(0);
  };

  useEffect(() => {
    load(debounced.text, page * debounced.size, debounced.size);
  }, [debounced, page, load]);

  const startNew = (presetName = "") => {
    setEditing({ ...empty, name: presetName || "" });
    // Drop ?new= so a refresh doesn't reopen a stale dialog.
    if (params.get("new") !== null) {
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  };

  // Hand-off from the billing client picker: /app/clients?new=Acme
  useEffect(() => {
    const preset = params.get("new");
    if (preset !== null) startNew(preset);
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      // No company_id: the client is created on the account, shared by all
      // company profiles.
      const body = { ...editing, company_id: "" };
      if (editing.id) await api.put(`/clients/${editing.id}`, body);
      else await api.post("/clients", body);
      toast.success("Client saved — available to every company");
      setEditing(null);
      await load(debounced.text, page * debounced.size, debounced.size);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client? Existing documents keep their saved copy of the details.")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      const nextPage = list.length === 1 && page > 0 ? page - 1 : page;
      setPage(nextPage);
      await load(debounced.text, nextPage * debounced.size, debounced.size);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pageCount = Math.ceil(total / debounced.size);
  const showEmpty = !loading && total === 0;
  const searching = debounced.text.length > 0;

  return (
    <>
      <PageHeader
        overline="People"
        title="Clients"
        description="Companies and people you invoice. Shared across all of your company profiles — add a client once and bill it from whichever company is active."
        testid="page-clients"
        actions={
          <button className="btn-primary text-sm inline-flex items-center gap-2" onClick={() => startNew()} data-testid="btn-new-client">
            <Plus className="w-4 h-4" /> New Client
          </button>
        }
      />
      <Content>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone or GSTIN…"
              className="w-full h-10 pl-9 pr-3 border border-[var(--border)] rounded-md bg-white text-sm"
              data-testid="clients-search"
            />
            {loading && (
              <Loader2 className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />
            )}
          </div>
          <div className="text-xs text-gray-500 num" data-testid="clients-count">
            {searching ? `${total} match(es)` : `${total} client(s) across all companies`}
          </div>
        </div>

        {showEmpty ? (
          <EmptyState
            title={searching ? "No matching clients" : "No clients yet"}
            description={
              searching
                ? `Nothing matches "${debounced.text}". Try a different name, email, phone or GSTIN.`
                : "Add the companies or people you invoice, so you don't retype their details every time. Clients are shared by all your companies."
            }
            action={!searching && <button className="btn-primary" onClick={() => startNew()}>Add your first client</button>}
            testid="empty-clients"
          />
        ) : (
          <div className="card-flat overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="text-left text-[11px] text-gray-500 uppercase tracking-wider">
                  <tr className="border-b border-[var(--border)]">
                    <th className="px-5 py-3 font-medium">Name</th>
                    <th className="px-5 py-3 font-medium">Email</th>
                    <th className="px-5 py-3 font-medium">Phone</th>
                    <th className="px-5 py-3 font-medium">City</th>
                    <th className="px-5 py-3 font-medium">GSTIN</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody data-testid="clients-table">
                  {list.map((c) => (
                    <tr key={c.id} className="border-b border-[var(--border)] hover:bg-gray-50">
                      <td className="px-5 py-3 font-medium">{text(c.name)}</td>
                      <td className="px-5 py-3 text-gray-600">{text(c.email)}</td>
                      <td className="px-5 py-3 text-gray-600">{text(c.phone)}</td>
                      <td className="px-5 py-3 text-gray-600">{text(c.city)}</td>
                      <td className="px-5 py-3 font-mono text-[12px] text-gray-600">{text(c.gstin)}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <Link to={`/app/clients/${c.id}/statement`} className="p-2 hover:bg-gray-100 rounded inline-flex" title="Statement" data-testid={`statement-${c.id}`}>
                          <FileText className="w-4 h-4" />
                        </Link>
                        <button className="p-2 hover:bg-gray-100 rounded" onClick={() => setEditing({ ...c })} title="Edit client" data-testid={`edit-client-${c.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button className="p-2 hover:bg-gray-100 rounded text-red-600" onClick={() => remove(c.id)} title="Delete client" data-testid={`delete-client-${c.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 pb-4">
              <TablePagination
                page={page}
                pageCount={pageCount}
                total={total}
                pageSize={debounced.size}
                onPage={setPage}
                onPageSize={changePageSize}
                idPrefix="clients"
                noun="client"
              />
            </div>
          </div>
        )}
      </Content>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <div className="overline">{editing.id ? "Edit" : "New"}</div>
                <div className="text-lg font-medium mt-0.5">Client</div>
              </div>
              <button className="p-1.5 hover:bg-gray-100 rounded" onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 pt-4 text-[11px] text-gray-500">
              Saved to your account and shared by every company profile.
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              {[
                { k: "name", label: "Name", span: 2, required: true },
                { k: "email", label: "Email" },
                { k: "phone", label: "Phone" },
                { k: "address", label: "Address", span: 2 },
                { k: "city", label: "City" },
                { k: "state", label: "State" },
                { k: "country", label: "Country" },
                { k: "postal_code", label: "Postal Code" },
                { k: "gstin", label: "GSTIN", span: 2 },
              ].map((f) => (
                <label key={f.k} className={`block ${f.span === 2 ? "col-span-2" : ""}`}>
                  <div className="text-[11px] text-gray-600 mb-1.5 uppercase tracking-wider">{f.label}</div>
                  <input value={editing[f.k] || ""} onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })}
                    className="w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm"
                    data-testid={`client-input-${f.k}`}
                    required={f.required} />
                </label>
              ))}
            </div>
            <div className="p-5 border-t border-[var(--border)] flex justify-end gap-2">
              <button className="px-4 h-10 border border-[var(--border)] rounded-md text-sm" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn-primary text-sm" onClick={save} disabled={busy || !editing.name} data-testid="client-save">
                {busy ? "Saving…" : "Save Client"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}